<script setup lang="ts">
import { ArrowDown, Timer } from 'lucide-vue-next'
import { formatMessage } from '~/lib/formatMessage'
import { activityStatusLabel } from '~/utils/activityStatusLabel'
import { messageCarriesCard, messageCards } from '~/types/web-chat'
import { generationDurationMs } from '~/utils/turnDuration'
import { formatMs } from '#shared/format/duration'
import type { WebChatMessage, ConfirmableItem, ToolActivity } from '~/types/web-chat'
import { toolActivityFor, type ToolActivityKind } from '~~/shared/web-chat/toolActivity'
import { matchesProviderToolCall } from '~/composables/web-chat/normalizers'
import type { GroundingRecoveryAction } from '~~/shared/chat/groundingRecovery'
import type { CitationSnippet } from '~/components/citation/types'
import CitationPopover from '~/components/CitationPopover.vue'
import ToolActivityChip from '~/components/ToolActivityChip.vue'
const { t } = useI18n()
const { openLawSection } = usePanelLayout()
const { profile } = useUserProfile()

// SUPERADMIN-only generation timer (how long the turn took to answer) — see ~/utils/turnDuration.
const isSuperAdmin = computed(() => !!profile.value?.is_superadmin)

const props = defineProps<{
  messages: WebChatMessage[]
  isStreaming: boolean
  /** The streaming turn's server-tool activity, set by the stream handlers (MAR-1057). */
  toolActivities?: ToolActivity[]
  /** The streaming turn waits for its answer (MAR-1057) — what the live line shows. */
  awaitingAnswer?: boolean
  citationSnippets?: Map<string, CitationSnippet>
  /** True while missing citation snippets are being fetched from the API (see useChatCitations). */
  citationsResolving?: boolean
  /** A shared snapshot (MAR-1417): render only, no edit/resend affordance on user messages. */
  readonly?: boolean
}>()

const visibleMessages = computed(() => {
  const lastMsg = props.messages[props.messages.length - 1]
  return props.messages.filter(m =>
    !m.hiddenFromUi && (
      m.content.length > 0
      || (m.segments && m.segments.length > 0)
      // Cards make a text-less assistant turn visible, and this check is one field test rather than
      // a list of them: a card missing from it doesn't just fail to render, it HANGS the
      // conversation — a confirm tool pauses the ADK runner until the browser answers. See the type.
      || messageCarriesCard(m)
      || (props.isStreaming && m === lastMsg)
    ),
  )
})

// ── Tool activity ──
// The live activity is the store's (`useChatStreaming` sets it where the stream says what changed,
// MAR-1057); this component only derives what each chip shows from the message's tool calls.

function historicalTools(msg: WebChatMessage, segIdx: number): ToolActivity[] {
  if (!msg.toolCalls) return []
  return msg.toolCalls
    .filter(tc => tc.segmentIndex === segIdx)
    .map((tc, i) => {
      const queries = toolQueries(tc.input)
      const { code, kind, params } = historicalActivity(tc, queries)
      const label = activityStatusLabel(t, code, params)

      return {
        id: tc.id || i,
        label,
        completed: true,
        segmentIndex: tc.segmentIndex,
        queries,
        details: quickResearchDetails(tc),
        kind,
      }
    })
}

// Reconstruct a localizable activity (code + kind + params) from a persisted tool call, mirroring
// the codes the server emits for the live streaming status. Two tools need bespoke handling that a
// static table can't express — `run_query_legal_catalog`'s running/done split depends on its OUTPUT,
// not just its name, and `query_legal_catalog`'s params are a query COUNT plus the first query text,
// not the generic single `query` string every other tool here takes. Everything else resolves
// through `toolActivityFor` (`shared/web-chat/toolActivity.ts`), the one table also used live
// (`remoteAgent.ts`) and for the group-summary label (`ToolActivityList.vue`).
function historicalActivity(
  tc: NonNullable<WebChatMessage['toolCalls']>[number],
  queries: string[],
): { code: string; kind: ToolActivityKind; params: Parameters<typeof activityStatusLabel>[2] } {
  const input = tc.input as Record<string, unknown>
  if (tc.name === 'run_query_legal_catalog') {
    const output = tc.output
    const done = output && typeof output === 'object' && 'activity_summary' in output
      && Array.isArray(output.activity_summary) && output.activity_summary.length > 0
    return { code: done ? 'query_legal_catalog_done' : 'query_legal_catalog_running', kind: 'legal', params: {} }
  }
  if (tc.name === 'query_legal_catalog') {
    return { code: 'legal_searching', kind: 'legal', params: { count: queries.length, query: queries[0] } }
  }
  const { code, kind } = toolActivityFor(tc.name)
  return { code, kind, params: { query: input.query as string } }
}

// Ordered per-call steps the quick-research sub-agent ran, from either the live `tool_result`
// (`activitySteps`) or the persisted tool-call output (`activity_steps`). Display-only.
function quickResearchActivitySteps(
  toolCall: NonNullable<WebChatMessage['toolCalls']>[number],
): string[] | undefined {
  const live = toolCall.activitySteps
  if (live?.length) return live.map(step => step.label).filter(Boolean)
  const output = toolCall.output
  if (output && typeof output === 'object' && 'activity_steps' in output) {
    const steps = (output as { activity_steps?: unknown }).activity_steps
    if (Array.isArray(steps)) {
      const labels = steps
        .map(step => step && typeof step === 'object' && 'label' in step ? String((step as { label: unknown }).label) : '')
        .filter(Boolean)
      if (labels.length > 0) return labels
    }
  }
  return undefined
}

// A `query_legal_catalog` query entry is either a plain string or a STRUCTURED_QUERY object
// (`{ texto, conceptos? }`, see server/tools/rag.ts) — never String() it directly or it renders as
// "[object Object]". Prefer the natural-language `texto`, fall back to the joined `conceptos`.
function queryLabel(q: unknown): string {
  if (typeof q === 'string') return q
  if (q && typeof q === 'object') {
    const r = q as Record<string, unknown>
    if (typeof r.texto === 'string' && r.texto.trim()) return r.texto
    if (typeof r.text === 'string' && r.text.trim()) return r.text
    if (Array.isArray(r.conceptos) && r.conceptos.length) return r.conceptos.map(String).join(', ')
    if (typeof r.query === 'string' && r.query.trim()) return r.query
  }
  return ''
}

function toolQueries(input: unknown): string[] {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return []
  const record = input as Record<string, unknown>
  if (Array.isArray(record.queries)) return record.queries.map(queryLabel).filter(Boolean)
  return typeof record.query === 'string' ? [record.query] : []
}

function quickResearchDetails(toolCall: NonNullable<WebChatMessage['toolCalls']>[number]): string[] | undefined {
  if (toolCall.subagentSteps?.length) return toolCall.subagentSteps
  // Ordered per-call steps (search → reads) the quick-research sub-agent ran — preferred over the
  // coarse `activity_summary` counts so the expanded panel shows the actual searches + reads.
  const steps = quickResearchActivitySteps(toolCall)
  if (steps?.length) return steps
  if (toolCall.activity?.length) {
    const activityLabels = toolCall.activity
      .map(item => item?.label ? String(item.label).replace(/\.{3}$/, '') : '')
      .filter(Boolean)
    if (activityLabels.length > 0) return activityLabels
  }
  const output = toolCall.output
  if (!output || typeof output !== 'object' || !('activity_summary' in output)) return undefined
  const summary = (output as { activity_summary?: unknown }).activity_summary
  if (!Array.isArray(summary)) return undefined
  const labels = summary
    .map(item => item && typeof item === 'object' && 'label' in item ? String((item as { label: unknown }).label) : '')
    .filter(Boolean)
  return labels.length > 0 ? labels : undefined
}

type LiveToolCall = NonNullable<WebChatMessage['toolCalls']>[number]

/** The tool call a live activity belongs to: by call id, else the latest call in its segment. */
function liveCallFor(activity: ToolActivity): LiveToolCall | undefined {
  const calls = props.messages[props.messages.length - 1]?.toolCalls ?? []
  if (activity.callId) {
    const exact = calls.find(tc => matchesProviderToolCall(tc, activity.callId!))
    if (exact) return exact
  }
  return [...calls].reverse().find(tc => tc.segmentIndex === activity.segmentIndex)
}

/** The streaming turn's activities with their queries, domain and research steps filled in. */
const liveActivities = computed<ToolActivity[]>(() => (props.toolActivities ?? []).map((activity) => {
  const call = liveCallFor(activity)
  if (!call) return activity
  return {
    ...activity,
    queries: activity.queries?.length ? activity.queries : toolQueries(call.input),
    kind: activity.kind ?? toolActivityFor(call.name).kind,
    details: quickResearchDetails(call),
  }
}))

/** The live line's tool, while one runs: its label, and the research question as its detail. */
const runningTool = computed(() => {
  const activity = liveActivities.value.find(a => !a.completed)
  if (!activity) return null
  const call = liveCallFor(activity)
  const request = call?.name === 'quick_research' ? (call.input as Record<string, unknown>)?.request : undefined
  return {
    label: activity.label,
    detail: typeof request === 'string' && request.trim() ? request.trim() : undefined,
    startedAt: activity.startedAt ?? Date.now(),
  }
})

// ── Computed states ──
function isStreamingMsg(msg: WebChatMessage) {
  return props.isStreaming && msg.role === 'assistant' && msg.id === visibleMessages.value[visibleMessages.value.length - 1]?.id
}

// The id of the assistant message currently streaming (null when idle). Drives the citation popover's
// per-message spinner: a not-yet-loaded citation shows a spinner only while ITS message is streaming.
const streamingMessageId = computed(() => {
  if (!props.isStreaming) return null
  const last = visibleMessages.value[visibleMessages.value.length - 1]
  return last?.role === 'assistant' ? last.id : null
})

// While a message streams its chips are the FINISHED tools; the running one is on the live line.
function segmentTools(msg: WebChatMessage, segIdx: number): ToolActivity[] {
  if (isStreamingMsg(msg)) {
    return liveActivities.value.filter(a => a.segmentIndex === segIdx && a.completed)
  }
  return historicalTools(msg, segIdx)
}

function currentSegmentTools(msg: WebChatMessage): ToolActivity[] {
  if (isStreamingMsg(msg)) {
    const idx = msg.segments?.length ?? 0
    return liveActivities.value.filter(a => a.segmentIndex === idx && a.completed)
  }
  const idx = msg.segments?.length ?? 0
  return historicalTools(msg, idx)
}


function showValidation(msg: WebChatMessage) {
  return msg.role === 'assistant'
    && !!msg.validation
    && msg.validation.status !== 'not_applicable'
}

// ── Citation popover ──
const citationPopoverRef = ref<InstanceType<typeof CitationPopover> | null>(null)
const snippetsMap = computed(() => props.citationSnippets ?? new Map<string, CitationSnippet>())

function onCitationNavigate(normId: string | null, sectionId: string, source?: { messageId: string }) {
  // Enrich the citation's back-reference with the current conversation id (A5) so the full-page law
  // view can link back to this conversation + message.
  const conversationId = useWebChatStore().conversationId
  openLawSection(normId, sectionId, source ? { ...source, conversationId } : undefined)
}

// ── Auto-scroll ──
const container = ref<HTMLElement | null>(null)

// Stick-to-bottom guard (A3): auto-scroll only follows the stream when the user is already parked at
// the bottom. Every wire event (deltas, "Buscando en la legislación…" status, tool cards) calls
// requestScroll(); without this guard each one yanked the viewport back down, so a user scrolling up to
// re-read got dragged to the bottom on every status tick. Once they scroll up we detach and stop
// forcing; scrolling back near the bottom re-attaches.
const STICK_THRESHOLD_PX = 80
const stickToBottom = ref(true)

function distanceFromBottom(el: HTMLElement) {
  return el.scrollHeight - el.scrollTop - el.clientHeight
}

function onContainerScroll() {
  const el = container.value
  if (!el) return
  stickToBottom.value = distanceFromBottom(el) <= STICK_THRESHOLD_PX
}

function scrollBottom() {
  nextTick(() => {
    const el = container.value
    if (!el) return
    if (!stickToBottom.value) return // user scrolled up — don't fight them
    el.scrollTop = el.scrollHeight
  })
}

/** Force the viewport to the bottom and re-attach (used by the "jump to latest" affordance / send). */
function scrollToBottomNow() {
  stickToBottom.value = true
  scrollBottom()
}

let scrollPending = false

function requestScroll() {
  if (scrollPending) return
  scrollPending = true
  const schedule = typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame
    : (callback: FrameRequestCallback) => setTimeout(callback, 0)
  schedule(() => {
    scrollPending = false
    scrollBottom()
  })
}

onMounted(() => {
  if (container.value && citationPopoverRef.value) {
    citationPopoverRef.value.setupListeners(container.value)
  }
})

// A5: arriving from the full-page law view ("Volver a la respuesta") with ?m=<messageId> scrolls to +
// highlights that message once it's in the DOM, then clears the param. The transcript loads async, so
// we retry a few frames (no watcher — a bounded self-cancelling poll) until the anchor appears.
const route = useRoute()
const router = useRouter()
onMounted(() => {
  const id = typeof route.query.m === 'string' ? route.query.m : ''
  if (!id) return
  let tries = 0
  const attempt = () => {
    const el = container.value?.querySelector(`[data-message-id="${CSS.escape(id)}"]`) as HTMLElement | null
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el.style.transition = 'background-color 0.4s ease'
      el.style.borderRadius = '0.5rem'
      el.style.backgroundColor = 'var(--color-accent-light)'
      setTimeout(() => { el.style.backgroundColor = '' }, 1200)
      const q = { ...route.query }
      delete q.m
      router.replace({ query: q })
      return
    }
    if (tries++ < 20) setTimeout(attempt, 150) // ~3s max, then give up
  }
  attempt()
})

const emit = defineEmits<{
  resend: [messageId: string, content: string]
  'card-accept': []
  'card-decline': []
  /** `run_workflow` only: send the prefilled proposal to the full run form (MAR-437). */
  'card-open-form': []
  'download-document': [item: ConfirmableItem]
  'fix-validation': [messageId: string, action: GroundingRecoveryAction]
  /** "Envíamelo por correo" on a queued Slow turn (MAR-1424). */
  'request-email': [messageId: string]
}>()

defineExpose({ requestScroll, scrollBottom, scrollToBottomNow })
</script>

<template>
  <CitationPopover
    ref="citationPopoverRef"
    :snippets="snippetsMap"
    :streaming-message-id="streamingMessageId"
    :citations-resolving="citationsResolving"
    @navigate="onCitationNavigate"
  />
  <!-- `pb-9`: the composer's 24 px fade overlaps the bottom of this scroller, so the last line (often
       the live line) needs that much more room to sit clear of it when scrolled to the end. -->
  <div ref="container" class="relative flex-1 overflow-y-auto px-3 pt-3 pb-9" @scroll.passive="onContainerScroll">
    <!-- Jump to latest (A3): appears only when the user has scrolled up and detached from the stream. -->
    <button
      v-if="!stickToBottom"
      type="button"
      class="sticky bottom-2 left-1/2 z-10 -translate-x-1/2 flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-md transition-colors hover:text-foreground"
      @click="scrollToBottomNow"
    >
      <ArrowDown class="h-3.5 w-3.5" />
      {{ t('webChat.jumpToLatest') }}
    </button>
    <div class="max-w-3xl mx-auto space-y-3">
    <!-- Messages: user (right-aligned) + assistant (left-aligned) -->
    <template v-for="msg in visibleMessages" :key="msg.id">
    <ChatUserMessage
      v-if="msg.role === 'user'"
      :msg="msg"
      :readonly="props.readonly"
      @resend="(id, content) => emit('resend', id, content)"
    />

    <!-- Assistant message, left-aligned and with no avatar (MAR-1057): the prose on the left already
         says who wrote it, and while Marcus works the live line's mark is the only Marcus mark.
         data-message-id anchors the A5 "return to the answer" scroll target for citations opened
         into the law view. -->
    <div v-else data-assistant-message class="group/msg flex items-start gap-2" :data-message-id="msg.id">

      <div class="flex-1 min-w-0">
        <!-- Previous iteration segments with their tool calls -->
        <div v-for="(seg, idx) in (msg.segments ?? [])" :key="'seg-'+idx" class="mb-2">
          <div
            v-if="seg.content"
            data-ph-mask
            class="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none marcus-prose text-muted-foreground"
            v-html="formatMessage(seg.content)"
          />
          <ChatToolActivityList
            :activities="segmentTools(msg, idx)"
            class="mt-1.5"
          />
        </div>

        <!-- Current/final content -->
        <div
          v-if="msg.content"
          data-ph-mask
          class="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none marcus-prose"
          v-html="formatMessage(msg.content)"
        />

        <!-- Active tool calls for current segment -->
        <ChatToolActivityList
          :activities="currentSegmentTools(msg)"
          :class="msg.content || (msg.segments ?? []).length > 0 ? 'mt-1.5' : ''"
        />

        <!-- The live line (MAR-1057): what Marcus is doing for the whole wait — thinking, or the tool it
             runs — below whatever the message already holds. The grounding badge has its own spinner. -->
        <!-- A queued Slow (Gemma) turn shows its stages instead (MAR-1424): place in line, cold start,
             research, writing — and "Envíamelo por correo". Only while it streams: a finished turn has
             no card and no button. -->
        <ChatQueuedTurnProgress
          v-if="isStreamingMsg(msg) && msg.queuedTurn"
          :progress="msg.queuedTurn"
          :detail="runningTool?.label"
          @request-email="emit('request-email', msg.id)"
        />
        <ChatThinkingIndicator
          v-else-if="isStreamingMsg(msg) && awaitingAnswer && msg.validation?.status !== 'running'"
          :activity="runningTool"
        />

        <!-- Streaming cursor -->
        <span
          v-else-if="isStreamingMsg(msg) && msg.content && msg.validation?.status !== 'running'"
          class="streaming-cursor inline-block w-0.5 h-[1.1em] bg-primary align-middle ml-0.5"
        />

        <!-- Superadmin generation timer (how long this turn took to answer) -->
        <div
          v-if="isSuperAdmin && !isStreamingMsg(msg) && generationDurationMs(msg) != null"
          class="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground/70 select-none"
          title="Tiempo de respuesta (solo super-admin)"
        >
          <Timer class="h-3 w-3 shrink-0" />
          <span>{{ formatMs(generationDurationMs(msg)!) }}</span>
        </div>

        <!-- The free plan's Fast allowance is spent: offer Slow (MAR-1390 round 3). -->
        <ChatSwitchToSlow v-if="msg.offerSlowTier && !isStreamingMsg(msg)" />

        <!-- Legal answer validation -->
        <ChatAnswerGroundingBadge
          v-if="showValidation(msg)"
          :validation="msg.validation!"
          :message-id="msg.id"
          :is-streaming="isStreaming"
          @fix-validation="(id, action) => emit('fix-validation', id, action)"
        />

        <!-- Every card this turn carries, in order — two result lists are two cards (MAR-1017).
             One component resolves the tool → its card. -->
        <template v-if="!isStreamingMsg(msg)">
          <ChatToolCard
            v-for="(card, cardIndex) in messageCards(msg)"
            :key="card.callId ?? `card-${cardIndex}`"
            :card="card"
            @accept="emit('card-accept')"
            @decline="emit('card-decline')"
            @download="emit('download-document', $event)"
            @open-form="emit('card-open-form')"
          />
        </template>

        <!-- Hover action buttons -->
        <ChatAssistantMessageActions
          v-if="msg.content && !isStreamingMsg(msg)"
          :msg="msg"
        />
      </div>
    </div>
    </template>
    </div>

  </div>
</template>

<style scoped>
@reference "~/assets/css/tailwind.css";
.streaming-cursor {
  animation: cursor-blink 530ms steps(1) infinite;
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.tool-chip {
  animation: chip-enter 0.2s ease-out;
}

@keyframes chip-enter {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
